// REFACTORED: 2026-02-03
// Projects page - Charts Section (Metrics, Health Matrix, Funding)

import React from 'react';
import { Grid, Box, Card, CardContent, Typography, alpha, Skeleton } from '@mui/material';
import { CombinedMetricsAndStatusChart, FundedByBreakdownChart } from '../../charts/ProjectCharts';
import { SEARCE_COLORS } from '../../../theme';
import { CombinedProjectHealthMatrix } from '../../charts/ProjectAnalyticsCharts';

interface ProjectChartsSectionProps {
  rag_health_matrix: any[];
  summaryMetrics: {
    total_projects: number;
    internal_projects: number;
    open_opportunities: number;
    avg_recognition: number;
  };
  statusCounts: {
    active: number;
    yet_to_start: number;
    completed: number;
    on_hold: number;
    cancelled: number;
    delayed_starts: number;
  };
  fundingAnalysis?: any;
  isLoading?: boolean;
}

/**
 * Three cards in one row (md+): Project Metrics donut, Project Health Matrix, Funding donut.
 */
const ProjectChartsSection: React.FC<ProjectChartsSectionProps> = ({
  summaryMetrics,
  rag_health_matrix,
  statusCounts,
  fundingAnalysis,
  isLoading = false,
}) => {
  if (isLoading) {
    return (
      <Grid container spacing={2} sx={{ alignItems: 'stretch', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
        {[1, 2, 3].map((i) => (
          <Grid item xs={12} md={4} key={i} sx={{ display: 'flex', minWidth: { xs: '100%', md: 0 }, flex: { md: '1 1 0' } }}>
            <Card variant="outlined" sx={{ p: 2, minHeight: 280, width: '100%', height: '100%', boxShadow: 'none', borderRadius: 2 }}>
              <Skeleton variant="text" width="65%" height={24} sx={{ mb: 2 }} animation="wave" />
              <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 1 }} animation="wave" />
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <Grid container spacing={2} sx={{ alignItems: 'stretch', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
      <Grid item xs={12} md={4} sx={{ display: 'flex', minWidth: { xs: '100%', md: 0 }, flex: { md: '1 1 0' } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', minWidth: 0, flex: 1 }}>
          <CombinedMetricsAndStatusChart metrics={summaryMetrics} statusCounts={statusCounts} />
        </Box>
      </Grid>

      <Grid item xs={12} md={4} sx={{ display: 'flex', minWidth: { xs: '100%', md: 0 }, flex: { md: '1 1 0' } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', minWidth: 0, flex: 1 }}>
          <CombinedProjectHealthMatrix data={rag_health_matrix} />
        </Box>
      </Grid>

      <Grid item xs={12} md={4} sx={{ display: 'flex', minWidth: { xs: '100%', md: 0 }, flex: { md: '1 1 0' } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', minWidth: 0, flex: 1 }}>
          {fundingAnalysis ? (
            <FundedByBreakdownChart fundingData={fundingAnalysis} />
          ) : (
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                background: `linear-gradient(135deg, ${SEARCE_COLORS.cardBackground} 0%, ${alpha(SEARCE_COLORS.primary, 0.02)} 100%)`,
                border: `1px solid ${alpha(SEARCE_COLORS.primary, 0.1)}`,
                transition: 'all 0.3s ease',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                flex: 1,
                '&:hover': {
                  boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <CardContent sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    color: SEARCE_COLORS.textPrimary,
                    fontSize: { xs: '1.125rem', sm: '1.25rem' },
                    mb: 1.5,
                  }}
                >
                  Funding Source Analysis
                </Typography>
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="body1" color="textSecondary">
                    No funding data available
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

export default ProjectChartsSection;
